import { forwardRef } from 'react'

export type QuantityStepperProps = {
  value: number
  onChange: (next: number) => void
  /** Accessible label for the stepper group — read by screen readers. */
  label: string
  min?: number
  max?: number
  className?: string
  disabled?: boolean
}

/**
 * Controlled +/- quantity stepper. Replaces the `<input type="number">` pattern
 * for cases where the legacy app uses a stepper (catalog cards). The component
 * clamps internally so callers don't have to repeat the min/max math.
 */
export const QuantityStepper = forwardRef<HTMLDivElement, QuantityStepperProps>(
  function QuantityStepper(
    { value, onChange, label, min = 1, max = 99, className = '', disabled = false },
    ref,
  ) {
    const dec = () => onChange(Math.max(min, value - 1))
    const inc = () => onChange(Math.min(max, value + 1))
    const cls = ['al-qty-stepper', className].filter(Boolean).join(' ')

    return (
      <div ref={ref} className={cls} role="group" aria-label={label}>
        <button
          type="button"
          className="al-qty-stepper__btn"
          onClick={dec}
          disabled={disabled || value <= min}
          aria-label="Decrease quantity"
        >
          −
        </button>
        <span className="al-qty-stepper__value" aria-live="polite">
          {value}
        </span>
        <button
          type="button"
          className="al-qty-stepper__btn"
          onClick={inc}
          disabled={disabled || value >= max}
          aria-label="Increase quantity"
        >
          +
        </button>
      </div>
    )
  },
)
